// src/components/SeanceDetailsModal.jsx
import React from "react";
import axios from "axios";
import ReactPlayer from "react-player";
import {
  Alert,
  Badge,
  Card,
  Descriptions,
  Image,
  Modal,
  Row,
  Col,
  Skeleton,
  Space,
  Tag,
  Typography,
  Empty,
} from "antd";
import { useTranslation } from "react-i18next";
import { ClockCircleOutlined, PlayCircleOutlined } from "@ant-design/icons";
import Constants from "../Utils/Constants";

const isMinioImageKey = (value) =>
  typeof value === "string" && value.startsWith("images/");

const isMinioVideoKey = (value) =>
  typeof value === "string" &&
  (value.startsWith("videos/") || value.startsWith("sessions/"));

const normalizeUrl = (url) => {
  if (!url) return null;

  if (url.startsWith("http")) {
    return url;
  }

  return `${Constants.SERVER_URL.replace(/\/$/, "")}/${url.replace(
    /^\/+/,
    ""
  )}`;
};

export default function SeanceDetailsModal({ open, seanceKey, onClose }) {
  const { t, i18n } = useTranslation("seances", { useSuspense: false });
  const token = localStorage.getItem("token");

  const headers = React.useMemo(
    () => ({
      Authorization: "Bearer " + token,
      "Accept-Language": i18n.language,
    }),
    [token, i18n.language]
  );

  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [err, setErr] = React.useState(null);

  const [sessionVideoUrl, setSessionVideoUrl] = React.useState(null);
  const [imageUrls, setImageUrls] = React.useState({});
  const [videoUrls, setVideoUrls] = React.useState({});

  const [videoPreviewOpen, setVideoPreviewOpen] = React.useState(false);
  const [videoPreviewUrl, setVideoPreviewUrl] = React.useState(null);

  const getSignedUrl = async (key, bucket) => {
    const res = await axios.get(
      `${Constants.SERVER_URL}/api/minio/presign-download`,
      {
        params: {
          bucket,
          key,
        },
        headers,
      }
    );

    return res.data.downloadUrl;
  };

  const openVideoPreview = (url) => {
    if (!url) return;

    setVideoPreviewUrl(url);
    setVideoPreviewOpen(true);
  };

  const hydrateSessionBlocs = async (sessionData) => {
    const blocSessions = Array.isArray(sessionData?.Bloc_Sessions)
      ? sessionData.Bloc_Sessions
      : [];

    const hydratedBlocSessions = await Promise.all(
      blocSessions.map(async (blocSession) => {
        const blocKey =
          blocSession?.Bloc?.key ||
          blocSession?.blocKey ||
          blocSession?.BlocKey;

        if (!blocKey) {
          return blocSession;
        }

        try {
          const blocRes = await axios.get(
            `${Constants.SERVER_URL}/bloc/${encodeURIComponent(blocKey)}`,
            { headers }
          );

          return {
            ...blocSession,
            Bloc: {
              ...blocSession.Bloc,
              ...blocRes.data,
            },
          };
        } catch {
          return blocSession;
        }
      })
    );

    return {
      ...sessionData,
      Bloc_Sessions: hydratedBlocSessions,
    };
  };

  React.useEffect(() => {
    if (!open || !seanceKey) return;

    setLoading(true);
    setErr(null);
    setData(null);
    setSessionVideoUrl(null);
    setImageUrls({});
    setVideoUrls({});
    setVideoPreviewOpen(false);
    setVideoPreviewUrl(null);

    axios
      .get(`${Constants.SERVER_URL}/session/${encodeURIComponent(seanceKey)}`, {
        headers,
      })
      .then(async (res) => {
        const sessionData = res.data || null;

        if (!sessionData) {
          setData(null);
          return;
        }

        const hydratedSession = await hydrateSessionBlocs(sessionData);
        setData(hydratedSession);
      })
      .catch((error) => {
        setErr(error);
      })
      .finally(() => {
        setLoading(false);
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, seanceKey, headers]);

  React.useEffect(() => {
    if (!data) return;

    let cancelled = false;

    async function loadMedia() {
      const imageEntries = [];
      const videoEntries = [];

      try {
        if (data.videoUrl) {
          if (isMinioVideoKey(data.videoUrl)) {
            const signedSessionVideoUrl = await getSignedUrl(
              data.videoUrl,
              "rxapa-videos"
            );

            if (!cancelled) {
              setSessionVideoUrl(signedSessionVideoUrl);
            }
          } else if (!cancelled) {
            setSessionVideoUrl(normalizeUrl(data.videoUrl));
          }
        }

        const blocSessions = Array.isArray(data.Bloc_Sessions)
          ? data.Bloc_Sessions
          : [];

        for (const blocSession of blocSessions) {
          const blocInfo = blocSession?.Bloc;

          if (!blocInfo) continue;

          const exerciseBlocs = Array.isArray(blocInfo.Exercise_Blocs)
            ? blocInfo.Exercise_Blocs
            : Array.isArray(blocInfo.exercises)
            ? blocInfo.exercises
            : Array.isArray(blocInfo.Exercises)
            ? blocInfo.Exercises
            : [];

          for (const exerciseBloc of exerciseBlocs) {
            const exerciseInfo =
              exerciseBloc?.Exercise || exerciseBloc?.exercise || exerciseBloc;

            if (!exerciseInfo?.id) continue;

            if (exerciseInfo.imageUrl) {
              try {
                const imageUrl = isMinioImageKey(exerciseInfo.imageUrl)
                  ? await getSignedUrl(exerciseInfo.imageUrl, "rxapa-assets")
                  : normalizeUrl(exerciseInfo.imageUrl);

                imageEntries.push([exerciseInfo.id, imageUrl]);
              } catch {
                imageEntries.push([exerciseInfo.id, null]);
              }
            }

            if (exerciseInfo.videoUrl) {
              try {
                const videoUrl = isMinioVideoKey(exerciseInfo.videoUrl)
                  ? await getSignedUrl(exerciseInfo.videoUrl, "rxapa-videos")
                  : normalizeUrl(exerciseInfo.videoUrl);

                videoEntries.push([exerciseInfo.id, videoUrl]);
              } catch {
                videoEntries.push([exerciseInfo.id, null]);
              }
            }
          }
        }

        if (!cancelled) {
          setImageUrls(Object.fromEntries(imageEntries));
          setVideoUrls(Object.fromEntries(videoEntries));
        }
      } catch {
        if (!cancelled) {
          setSessionVideoUrl(null);
          setImageUrls({});
          setVideoUrls({});
        }
      }
    }

    loadMedia();

    return () => {
      cancelled = true;
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return (
    <>
      <Modal
        open={open}
        onCancel={onClose}
        onOk={onClose}
        okText={t("label_close") || "Fermer"}
        title={
          data?.name
            ? `${t("label_session_info") || "Informations de la séance"} : ${
                data.name
              }`
            : t("label_session_info") || "Informations de la séance"
        }
        destroyOnClose
        width={980}
      >
        {loading ? (
          <Skeleton active paragraph={{ rows: 6 }} />
        ) : err ? (
          <Alert
            type="error"
            message={
              t("label_error_session_loading") ||
              "Erreur lors du chargement de la séance."
            }
          />
        ) : !data ? (
          <Alert
            type="warning"
            message={t("label_session_not_found") || "Séance introuvable."}
          />
        ) : (
          <Space direction="vertical" size={16} style={{ width: "100%" }}>
            <Descriptions bordered size="small" column={2}>
              <Descriptions.Item
                label={t("label_description") || "Description"}
                span={2}
              >
                {data.description || "—"}
              </Descriptions.Item>

              <Descriptions.Item
                label={t("label_video") || "Vidéo de la séance"}
                span={2}
              >
                {sessionVideoUrl ? (
                  <Typography.Link
                    onClick={() => openVideoPreview(sessionVideoUrl)}
                  >
                    <PlayCircleOutlined />{" "}
                    {t("label_view_video") || "Voir la vidéo"}
                  </Typography.Link>
                ) : (
                  "—"
                )}
              </Descriptions.Item>
            </Descriptions>

            <Typography.Title level={5} style={{ margin: 0 }}>
              {t("label_session_blocs") || "Blocs de la séance"}
            </Typography.Title>

            {Array.isArray(data.Bloc_Sessions) &&
            data.Bloc_Sessions.length > 0 ? (
              <Row gutter={[16, 16]}>
                {data.Bloc_Sessions.map((blocSession) => {
                  const blocInfo = blocSession?.Bloc;

                  if (!blocInfo) return null;

                  const exerciseBlocs = Array.isArray(blocInfo.Exercise_Blocs)
                    ? blocInfo.Exercise_Blocs
                    : Array.isArray(blocInfo.exercises)
                    ? blocInfo.exercises
                    : Array.isArray(blocInfo.Exercises)
                    ? blocInfo.Exercises
                    : [];

                  return (
                    <Col
                      key={blocSession.BlocId || blocInfo.id}
                      xs={24}
                      sm={12}
                    >
                      <Card>
                        <Space
                          direction="vertical"
                          size={8}
                          style={{ width: "100%" }}
                        >
                          <Typography.Text
                            strong
                            ellipsis
                            title={blocInfo.name}
                          >
                            {blocInfo.name || "Bloc"}
                          </Typography.Text>

                          {blocInfo.description && (
                            <Typography.Paragraph ellipsis={{ rows: 3 }}>
                              {blocInfo.description}
                            </Typography.Paragraph>
                          )}

                          <Typography.Text strong>
                            {t("label_bloc_exercises") ||
                              "Exercices du bloc"}
                          </Typography.Text>

                          {exerciseBlocs.length > 0 ? (
                            exerciseBlocs.map((exerciseBloc) => {
                              const exerciseInfo =
                                exerciseBloc?.Exercise ||
                                exerciseBloc?.exercise ||
                                exerciseBloc;

                              const pivot =
                                exerciseBloc?.Exercise_Bloc ||
                                exerciseBloc?.pivot ||
                                exerciseBloc;

                              if (!exerciseInfo?.name) {
                                return (
                                  <Alert
                                    key={
                                      exerciseBloc?.key ||
                                      exerciseBloc?.id ||
                                      Math.random()
                                    }
                                    type="warning"
                                    message="Exercice introuvable dans ce bloc."
                                  />
                                );
                              }

                              const exerciseImageUrl =
                                imageUrls[exerciseInfo.id];
                              const exerciseVideoUrl =
                                videoUrls[exerciseInfo.id];

                              return (
                                <Badge.Ribbon
                                  key={pivot.key || exerciseInfo.id}
                                  text={
                                    pivot.required
                                      ? t("label_required") || "Obligatoire"
                                      : t("label_optional") || "Optionnel"
                                  }
                                  color={pivot.required ? "red" : "blue"}
                                >
                                  <Descriptions
                                    bordered
                                    size="small"
                                    column={2}
                                    style={{ marginBottom: 16 }}
                                  >
                                    <Descriptions.Item
                                      span={2}
                                      label=""
                                      labelStyle={{ display: "none" }}
                                      contentStyle={{ textAlign: "center" }}
                                    >
                                      {exerciseImageUrl ? (
                                        <Image
                                          src={exerciseImageUrl}
                                          alt={exerciseInfo.name}
                                          height={160}
                                          style={{ objectFit: "cover" }}
                                          preview
                                        />
                                      ) : (
                                        <Empty
                                          image={Empty.PRESENTED_IMAGE_SIMPLE}
                                          description="Aucune image"
                                        />
                                      )}
                                    </Descriptions.Item>

                                    <Descriptions.Item
                                      label={t("label_name") || "Nom"}
                                      labelStyle={{ fontWeight: "bold" }}
                                      contentStyle={{ fontWeight: "bold" }}
                                      span={2}
                                    >
                                      {exerciseInfo.name || "—"}
                                    </Descriptions.Item>

                                    <Descriptions.Item
                                      label={
                                        t("label_description") ||
                                        "Description"
                                      }
                                      span={2}
                                    >
                                      {exerciseInfo.description || "—"}
                                    </Descriptions.Item>

                                    <Descriptions.Item
                                      label={
                                        t("label_instructions") ||
                                        "Instructions"
                                      }
                                      span={2}
                                    >
                                      <Row gutter={[8, 8]}>
                                        <Col span={12}>
                                          {typeof pivot.numberOfSeries ===
                                            "number" && (
                                            <Tag>
                                              📦 {pivot.numberOfSeries}{" "}
                                              {t("label_series") || "séries"}
                                            </Tag>
                                          )}
                                        </Col>

                                        <Col span={12}>
                                          {typeof pivot.numberOfRepetition ===
                                            "number" && (
                                            <Tag>
                                              🔁 {pivot.numberOfRepetition}{" "}
                                              {t("label_reps") || "reps"}
                                            </Tag>
                                          )}
                                        </Col>

                                        <Col span={12}>
                                          {typeof pivot.minutes === "number" &&
                                            pivot.minutes > 0 && (
                                              <Tag
                                                icon={<ClockCircleOutlined />}
                                              >
                                                {pivot.minutes}{" "}
                                                {t("label_minutes") || "min"}
                                              </Tag>
                                            )}
                                        </Col>

                                        <Col span={12}>
                                          {pivot.restingInstruction ? (
                                            <Tag>
                                              🛑{" "}
                                              {t("label_rest") || "Repos"}:{" "}
                                              {String(
                                                pivot.restingInstruction
                                              )}{" "}
                                              sec
                                            </Tag>
                                          ) : null}
                                        </Col>

                                        <Col span={24}>
                                          {exerciseVideoUrl ? (
                                            <Typography.Link
                                              onClick={() =>
                                                openVideoPreview(
                                                  exerciseVideoUrl
                                                )
                                              }
                                            >
                                              <PlayCircleOutlined />{" "}
                                              {t("label_view_video") ||
                                                "Voir la vidéo"}
                                            </Typography.Link>
                                          ) : null}
                                        </Col>
                                      </Row>
                                    </Descriptions.Item>
                                  </Descriptions>
                                </Badge.Ribbon>
                              );
                            })
                          ) : (
                            <Alert
                              type="info"
                              message={
                                t("label_no_exercises") ||
                                "Aucun exercice dans ce bloc."
                              }
                            />
                          )}
                        </Space>
                      </Card>
                    </Col>
                  );
                })}
              </Row>
            ) : (
              <Alert
                type="info"
                message={t("label_no_blocs") || "Aucun bloc pour l'instant"}
              />
            )}
          </Space>
        )}
      </Modal>

      <Modal
        open={videoPreviewOpen}
        onCancel={() => {
          setVideoPreviewOpen(false);
          setVideoPreviewUrl(null);
        }}
        footer={null}
        title="Aperçu vidéo"
        width={900}
        destroyOnClose
      >
        {videoPreviewUrl ? (
          <div style={{ aspectRatio: "16/9" }}>
            <ReactPlayer
              url={videoPreviewUrl}
              controls
              width="100%"
              height="100%"
            />
          </div>
        ) : (
          <Empty description="Impossible d’afficher la vidéo." />
        )}
      </Modal>
    </>
  );
}